import { Box, Button, Card, CardActionArea, Chip, Divider, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ClockIcon, WaitlistIcon } from '@tix-ticketing/ui-icons/outline';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedTime } from 'react-intl';
import type { EventDto } from 'api/generated';
import { useAppState } from 'lib/app-state';
import { clientTimezone, getLocalTimezoneAbbr } from 'lib/helpers/date-helpers';
import { FormattedPrice } from 'lib/helpers/formatted-price';
import { useServerTimezone } from 'lib/hooks/use-server-timezone';
import { DateCircle } from './date-circle';
import { WaitingListDialog } from './waiting-list-dialog';
interface EventDateCardProps {
  event?: NonNullable<EventDto>;
  onClick?: () => void;
  href?: string;
}
export function EventDateCard({
  event,
  onClick,
  href
}: EventDateCardProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const appState = useAppState();
  const router = useRouter();
  const {
    formatDate,
    formatTime,
    timezone: serverTimezone
  } = useServerTimezone();
  const [waitingListOpen, setWaitingListOpen] = useState(false);

  // Deep link support: ?waitingList={eventId} opens this card's dialog on
  // load, e.g. from a marketing link or a support/QA shortcut.
  useEffect(() => {
    const eligible = !!event && event.isFree !== true && event.saleStatus === 'SoldOut' && event.hasWaitingList === true;
    if (eligible && router.query.waitingList === String(event?.id)) {
      setWaitingListOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id, event?.isFree, event?.saleStatus, event?.hasWaitingList, router.query.waitingList]);
  if (!event) {
    return null;
  }
  const date = new Date(event.startDate ?? 0);
  const now = new Date();
  const saleStartDate = event.saleStarts ? new Date(event.saleStarts) : null;
  const saleEndDate = event.saleEnds ? new Date(event.saleEnds) : null;
  const saleNotStarted = saleStartDate ? saleStartDate > now : false;
  const saleEnded = saleEndDate ? saleEndDate <= now : false;
  const isFree = event.isFree === true;
  // Free-admission events have no seats and cannot be bought. Their saleStatus
  // is irrelevant, so it never greys the card or affects interactivity: the
  // card keeps the same look as a sale-not-started event (full colour, no hover
  // highlight) but is not clickable, with a "Free admission" tag and info message.
  const interactive = !isFree;
  const eventDisabled = !isFree && (event.saleStatus === 'Cancelled' || event.saleStatus === 'SoldOut' || event.saleStatus === 'NotScheduled' || saleEnded);
  // Gates both the "Join waiting list" button and the dialog it opens, so the
  // button is only ever shown when there is a waiting list to join.
  const canJoinWaitingList = !isFree && event.saleStatus === 'SoldOut' && event.hasWaitingList === true;

  // Keeps ?waitingList={eventId} in sync with the dialog so the URL is
  // shareable/bookmarkable while it's open, and clean again once it's closed.
  const openWaitingList = () => {
    setWaitingListOpen(true);
    void router.replace({
      pathname: router.pathname,
      query: {
        ...router.query,
        waitingList: String(event.id)
      }
    }, undefined, {
      shallow: true
    });
  };
  const closeWaitingList = () => {
    setWaitingListOpen(false);
    const nextQuery = {
      ...router.query
    };
    delete nextQuery.waitingList;
    void router.replace({
      pathname: router.pathname,
      query: nextQuery
    }, undefined, {
      shallow: true
    });
  };
  return <Card
  // A disabled row is a flat block whatever the theme's card style; the
  // rest take that style.
  variant={eventDisabled ? 'contained' : undefined} key={event.id} sx={[{
    marginBottom: 1.5
  }, eventDisabled && {
    color: 'text.disabled'
  },
  // A row that can't be followed or opened yet drops the outlined
  // card's hover ring.
  (!interactive || saleNotStarted) && {
    '&:hover': {
      outlineColor: 'transparent'
    }
  }]}>
      <CardActionArea disabled={interactive && (eventDisabled || saleNotStarted)} sx={{
      position: 'relative',
      padding: {
        xs: 2,
        sm: 3
      },
      display: 'flex',
      flexDirection: {
        xs: 'column',
        sm: 'row'
      },
      gap: {
        sm: 3
      },
      textDecoration: 'none',
      alignItems: 'stretch',
      cursor: interactive ? undefined : 'default',
      // MUI's hover overlay, which a row that can't be followed drops.
      ...(!interactive && {
        '.MuiCardActionArea-focusHighlight': {
          backgroundColor: 'transparent'
        }
      })
    }} {...interactive && href ? {
      component: Link,
      href
    } : {
      component: 'div'
    }} onClick={interactive ? onClick : undefined}>
        <DateCircle disabled={eventDisabled} value={date} />
        <Divider orientation="vertical" flexItem />
        <Box sx={{
        paddingLeft: {
          sm: 1
        },
        paddingRight: {
          sm: 5
        },
        // Fixed width so the title column starts at the same position on
        // every row regardless of how long the date or venue name is.
        width: {
          sm: 200
        },
        flexShrink: 0,
        display: 'flex',
        alignItems: {
          xs: 'center',
          sm: 'flex-start'
        },
        flexDirection: {
          xs: 'row',
          sm: 'column'
        }
      }}>
          <Typography variant={isMobile ? 'body2Medium' : 'body1Medium'} sx={{
          fontWeight: 700
        }}>
            {formatDate(date, {
            dateStyle: 'long'
          })}
          </Typography>
          <Box sx={{
          display: {
            xs: 'block',
            sm: 'none'
          },
          fontVariant: 'body2Medium',
          fontWeight: 700,
          color: 'text.disabled',
          mx: '.6rem'
        }}>
            {'•'}
          </Box>
          <Box sx={{
          display: {
            xs: 'contents',
            sm: 'flex'
          },
          flexDirection: 'column',
          gap: '10px',
          mt: 'auto'
        }}>
            <Typography variant={isMobile ? 'body2Medium' : 'body1'} sx={[isMobile ? {
            fontWeight: 700
          } : {
            fontWeight: null
          }]}>
              {formatTime(date, {
              timeStyle: 'short'
            })}
            </Typography>
            <Typography variant={isMobile ? 'body3' : 'body2'} sx={{
            color: 'text.secondary',
            display: {
              xs: 'none',
              sm: 'block'
            }
          }}>
              {event.hallName}
            </Typography>
          </Box>
        </Box>
        <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1
      }}>
          <Typography variant="body1Medium" sx={{
          fontWeight: '700'
        }}>
            {event.name}
          </Typography>
          <Typography variant={isMobile ? 'caption' : 'body1'} sx={{
          xs: {
            fontSize: 11,
            lineHeight: 1.4,
            letterSpacing: 0.15,
            fontWeight: 400
          }
        }}>
            {event.subTitle}
          </Typography>

          <Box sx={{
          mt: 'auto',
          display: 'flex',
          justifyContent: 'space-between'
        }}>
            <Typography variant={isMobile ? 'body3' : 'body2'} sx={{
            color: 'text.secondary',
            display: {
              xs: 'block',
              sm: 'none'
            }
          }}>
              {event.hallName}
            </Typography>
            <Typography variant={isMobile ? 'body3' : 'body2'} sx={{
            color: 'text.secondary'
          }}>
              {isFree ? <FormattedMessage id="event.freeAdmission" defaultMessage={[{
              "type": 0,
              "value": "Admission to this event is free!"
            }]} /> : isMobile ? <FormattedMessage id="common.ticketsFromAmountShort" defaultMessage={[{
              "type": 0,
              "value": "from "
            }, {
              "type": 1,
              "value": "formattedAmount"
            }]} values={{
              formattedAmount: <FormattedPrice key="amount" amount={event.minPrice ?? 0} />
            }} /> : <FormattedMessage id="common.ticketsFromAmount" defaultMessage={[{
              "type": 0,
              "value": "Tickets from "
            }, {
              "type": 1,
              "value": "formattedAmount"
            }]} values={{
              formattedAmount: <FormattedPrice key="amount" amount={event.minPrice ?? 0} />
            }} />}
            </Typography>
          </Box>
        </Box>
        <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        justifyContent: 'space-between'
      }}>
          <Box sx={{
          position: {
            xs: 'absolute',
            sm: 'static'
          },
          top: 20,
          right: 20
        }}>
            {isFree ? <Chip color="default" label={<FormattedMessage id="event.freeAdmissionTag" defaultMessage={[{
            "type": 0,
            "value": "Free admission"
          }]} />} size="small" variant="filled" sx={{
            marginBottom: 1.5,
            cursor: 'inherit',
            fontWeight: 500
          }} /> : event.saleStatus === 'Cancelled' ? <Chip size="small" label={<FormattedMessage id="common.cancelled" defaultMessage={[{
            "type": 0,
            "value": "Cancelled"
          }]} />} sx={{
            marginBottom: 1.5,
            cursor: 'inherit',
            fontWeight: 500,
            color: 'text.disabled'
          }} /> : event.saleStatus === 'SoldOut' ? <Chip color="error" size="small" label={<FormattedMessage id="common.soldOut" defaultMessage={[{
            "type": 0,
            "value": "Sold out"
          }]} />} sx={{
            marginBottom: 1.5,
            cursor: 'inherit',
            fontWeight: 500
          }} /> : event.saleStatus === 'FewLeft' ? <Chip color="warning" label={<FormattedMessage id="common.fewLeft" defaultMessage={[{
            "type": 0,
            "value": "Few left"
          }]} />} size="small" sx={{
            marginBottom: 1.5,
            cursor: 'inherit',
            fontWeight: 500
          }} /> : event.saleStatus === 'NotScheduled' ? <Chip color="default" label={<FormattedMessage id="event.saleNotScheduled" defaultMessage={[{
            "type": 0,
            "value": "Sale not scheduled"
          }]} />} size="small" variant="filled" sx={{
            marginBottom: 1.5,
            cursor: 'inherit',
            fontWeight: 500
          }} /> : saleNotStarted ? <Chip color="default" label={<FormattedMessage id="event.saleNotStarted" defaultMessage={[{
            "type": 0,
            "value": "Sale not started"
          }]} />} size="small" variant="filled" sx={{
            marginBottom: 1.5,
            cursor: 'inherit',
            fontWeight: 500
          }} /> : saleEnded ? <Chip color="default" label={<FormattedMessage id="event.saleEnded" defaultMessage={[{
            "type": 0,
            "value": "Sale ended"
          }]} />} size="small" variant="filled" sx={{
            marginBottom: 1.5,
            cursor: 'inherit',
            fontWeight: 500
          }} /> : undefined}
          </Box>
          {canJoinWaitingList ? <Button variant="contained" size="medium" color="monochrome" sx={{
          width: {
            xs: '100%',
            sm: 'auto'
          },
          mt: {
            xs: 2,
            sm: 0
          },
          // The enclosing CardActionArea is disabled for a sold-out event,
          // which sets pointer-events: none on all its children; re-enable
          // it here so this button stays clickable.
          pointerEvents: 'auto'
        }} startIcon={<WaitlistIcon />} onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          openWaitingList();
        }}>
              <FormattedMessage id="event.joinWaitingList" defaultMessage={[{
            "type": 0,
            "value": "Join waiting list"
          }]} />
            </Button> : isFree ? null : saleStartDate && saleNotStarted ? <Stack direction="row" sx={{
          alignItems: 'center',
          gap: 1
        }}>
              <ClockIcon sx={{
            width: 16
          }} />
              <Typography variant="body2">
                <FormattedMessage id="event.saleStartsOnDate" defaultMessage={[{
              "type": 0,
              "value": "Sale starts "
            }, {
              "type": 1,
              "value": "startDate"
            }, {
              "type": 0,
              "value": " at "
            }, {
              "type": 1,
              "value": "time"
            }]} values={{
              startDate: <FormattedDate key="date" value={saleStartDate} dateStyle="long" timeZone={clientTimezone} />,
              time: <>
                        <FormattedTime key="time" value={saleStartDate} timeStyle="short" timeZone={clientTimezone} />
                        {clientTimezone !== serverTimezone ? ` ${getLocalTimezoneAbbr(saleStartDate, appState.locale)}` : ''}
                      </>
            }} />
              </Typography>
            </Stack> : null}
        </Box>
      </CardActionArea>
      {canJoinWaitingList && <WaitingListDialog open={waitingListOpen} onClose={closeWaitingList} eventId={event.id!} eventName={event.name} eventStartDate={event.startDate} maxTickets={event.waitingListMaxTickets} />}
    </Card>;
}